import { Auth0Client } from '@auth0/auth0-spa-js';
import { AuthProvider } from 'react-admin';
import { getAppConfig } from '@/config/AppConfig';

const appConfig = getAppConfig();

export const auth0Client = new Auth0Client({
  domain: appConfig.auth0Domain!,
  clientId: appConfig.auth0ClientId,
  cacheLocation: 'localstorage',
  useRefreshTokens: true,
  authorizationParams: {
    audience: appConfig.auth0Audience,
    scope: 'openid profile email offline_access',
    hd: 'getmoments.app',
    connection: 'google-oauth2',
  },
});

const { loginRedirectUrl } = appConfig;
const REQUIRED_EMAIL_DOMAIN = 'getmoments.app';

// Guard to prevent multiple concurrent calls to handleRedirectCallback
let callbackProcessingPromise: Promise<void> | null = null;

// Validate email domain and log out if invalid
const validateEmailDomain = async (): Promise<void> => {
  const user = await auth0Client.getUser();
  if (!user?.email) {
    throw new Error('Failed to get user email.');
  }

  const emailDomain = user.email.split('@')[1];
  if (emailDomain !== REQUIRED_EMAIL_DOMAIN) {
    console.error(
      `[authProvider] Invalid email domain: ${emailDomain}. Required: ${REQUIRED_EMAIL_DOMAIN}`,
    );

    // Clear cache and log out
    const auth0Keys = Object.keys(localStorage).filter(
      key =>
        key.startsWith('@@auth0spajs@@') ||
        key.includes('auth0') ||
        key.includes('auth0spa'),
    );
    auth0Keys.forEach(key => localStorage.removeItem(key));

    const loginUrl = `${window.location.origin}${loginRedirectUrl}#/login`;
    await auth0Client.logout({
      logoutParams: {
        returnTo: loginUrl,
      },
    });

    throw new Error(
      `Access denied: Only ${REQUIRED_EMAIL_DOMAIN} email addresses are allowed.`,
    );
  }
};

export const loginWithAuth0 = () => {
  // Check if we just logged out - if so, force login prompt
  const justLoggedOut = sessionStorage.getItem(
    'admin_just_logged_out',
  );

  auth0Client
    .loginWithRedirect({
      authorizationParams: {
        redirect_uri: `${window.location.origin}${loginRedirectUrl}`,
        audience: appConfig.auth0Audience,
        scope: 'openid profile email offline_access',
        hd: 'getmoments.app',
        connection: 'google-oauth2',
        ...(justLoggedOut ? { prompt: 'login' } : {}), // Force login prompt after logout
      },
    })
    .catch(() => {
      // Ignore errors - redirect will happen anyway
    });
};

export const realmAuthProvider: AuthProvider = {
  async login() {
    return undefined;
  },
  async handleCallback() {
    const query = window.location.search;
    if (!query.includes('code=') || !query.includes('state=')) {
      return;
    }
    await auth0Client.handleRedirectCallback();
    // Validate email domain after callback
    await validateEmailDomain();
    // Clear the logout flag after successful login
    sessionStorage.removeItem('admin_just_logged_out');
  },
  async checkError(error: any) {
    const status = error?.status;
    const errorMessage = error?.message || '';

    // Don't log out on data provider errors - these are application errors, not auth errors
    if (
      errorMessage.includes('data_provider_error') ||
      errorMessage.includes('dataProvider') ||
      errorMessage.includes('getList') ||
      errorMessage.includes('id') ||
      error?.name === 'Error' && errorMessage.includes('data')
    ) {
      console.error(
        '[authProvider] checkError: Data provider error (not logging out):',
        error,
      );
      // Return without throwing - this prevents logout
      return;
    }

    if (status === 401 || status === 403) {
      throw new Error('Unauthorized');
    }

    // If error doesn't have a status, check if it's a known non-auth error
    if (status === undefined || status === null) {
      console.error(
        '[authProvider] checkError: Error without status:',
        error,
      );

      // Only log out on actual auth errors, not application/data errors
      // Check if it's explicitly an auth-related error
      if (
        errorMessage.includes('Unauthorized') ||
        errorMessage.includes('Forbidden') ||
        errorMessage.includes('Authentication') ||
        errorMessage.includes('Token') ||
        errorMessage.includes('Auth0')
      ) {
        throw new Error('Unauthorized');
      }

      // For other errors without status, don't log out
      // They'll be handled by error boundaries or error pages
      console.warn(
        '[authProvider] checkError: Non-auth error without status, not logging out:',
        error,
      );
      return;
    }
  },
  async checkAuth() {
    // If we're in a callback flow, process it first
    const query = window.location.search;
    if (query.includes('code=') && query.includes('state=')) {
      // Check if already authenticated - if so, callback was already processed
      const isAlreadyAuthenticated =
        await auth0Client.isAuthenticated();
      if (isAlreadyAuthenticated) {
        // Callback already processed, just ensure token is available
        await auth0Client.getTokenSilently({
          authorizationParams: {
            audience: appConfig.auth0Audience,
          },
        });
        // Validate email domain
        await validateEmailDomain();
        return;
      }

      // Guard against multiple concurrent calls to handleRedirectCallback
      // If a callback is already being processed, wait for it
      if (!callbackProcessingPromise) {
        callbackProcessingPromise = (async () => {
          try {
            // React Admin only calls handleCallback on /auth-callback route
            // Since Auth0 redirects to a different URL, we need to call it manually
            await auth0Client.handleRedirectCallback();

            // Validate email domain after callback
            await validateEmailDomain();

            // Clear the logout flag after successful login
            sessionStorage.removeItem('admin_just_logged_out');

            // Clean up URL by removing callback query parameters
            const url = new URL(window.location.href);
            url.searchParams.delete('code');
            url.searchParams.delete('state');
            window.history.replaceState({}, '', url.toString());
          } finally {
            // Clear the promise after processing (success or failure)
            callbackProcessingPromise = null;
          }
        })();
      }

      // Wait for the callback processing to complete
      await callbackProcessingPromise;

      // After processing callback, check auth again
      const isAuthenticated = await auth0Client.isAuthenticated();
      if (isAuthenticated) {
        return;
      }
      throw new Error('Not authenticated');
    }

    // Check if we just logged out - if so, reject to prevent immediate re-auth
    const justLoggedOut = sessionStorage.getItem(
      'admin_just_logged_out',
    );
    if (justLoggedOut) {
      sessionStorage.removeItem('admin_just_logged_out');
      throw new Error('Not authenticated');
    }

    const isAuthenticated = await auth0Client.isAuthenticated();
    if (isAuthenticated) {
      // Ensure token is fetched and refresh token is stored
      await auth0Client.getTokenSilently({
        authorizationParams: {
          audience: appConfig.auth0Audience,
        },
      });
      // Validate email domain on every auth check
      await validateEmailDomain();
      return;
    }
    throw new Error('Not authenticated');
  },
  async logout() {
    // Set flag to force login prompt on next login attempt
    sessionStorage.setItem('admin_just_logged_out', 'true');

    // Clear Auth0's cache from localStorage BEFORE logout
    // This prevents isAuthenticated() from returning true immediately after
    const auth0Keys = Object.keys(localStorage).filter(
      key =>
        key.startsWith('@@auth0spajs@@') ||
        key.includes('auth0') ||
        key.includes('auth0spa'),
    );
    auth0Keys.forEach(key => localStorage.removeItem(key));

    // Always redirect to login page, even if not authenticated
    const loginUrl = `${window.location.origin}${loginRedirectUrl}#/login`;

    // Check if authenticated before logging out
    const auth0Authenticated = await auth0Client.isAuthenticated();

    if (auth0Authenticated) {
      await auth0Client.logout({
        logoutParams: {
          returnTo: loginUrl,
          // Don't use federated: true - it logs out entire Google account
        },
      });
    } else {
      // Not authenticated, but still redirect to login page
      window.location.href = loginUrl;
    }
  },
  async getIdentity() {
    if (await auth0Client.isAuthenticated()) {
      const user = await auth0Client.getUser();
      if (!user) {
        throw new Error('Failed to get identity.');
      }
      return {
        id: user.sub || user.email || '',
        fullName: user.name || user.email || '',
        email: user.email || '',
        avatar: user.picture,
      };
    }
    throw new Error('Failed to get identity.');
  },
  async getPermissions() {
    return [];
  },
};
